
import React, { useState, useContext } from "react"
import { ChevronUp, Info, Check } from "lucide-react"

import RegistrationContext from "../../contexts/RegistrationContext";


interface SummaryWrapperProps {
  expanded?: boolean
}

const SummaryWrapper: React.FC<SummaryWrapperProps> = ({ expanded = false }) => {

  const { cart, registrationType, optionAccess,registrationCode } =
    useContext(RegistrationContext);
  var registrationTypeString = "Full Delegate"; // "Full Delegate" | "Student" | "Student with Paper" | "Industry Day"
  var accessString = "Full Conference"; // "Full Conference" | "Workshops and Tutorials"
  if (optionAccess === "workshops") {
    accessString = "Workshops/Tutorials/DC";
  }
  if (registrationType === "student") registrationTypeString = "Student";
  if (registrationType === "student-paper") {
    registrationTypeString = "Student with Paper";
    accessString = "Full Conference";
  }


  if (registrationType === "industry") {
    registrationTypeString = "Industry Day";
    accessString = "Industry Day";
  }


  if (registrationType === "exhibitor") {
    registrationTypeString = "Exhibitor Pass";
    accessString = "Exhibitor Pass";
  }

  
  return (
    <>
      <RegistrationSummary
        registrationCode={registrationCode}
        registrationType={registrationTypeString}
        accessType={accessString}
        additionalOptions={{
          invitationLetterAdmin: true,
          invitationLetterVisa: cart.invitation_letter,
          banquetEntries: cart.extra_banquet,
          studentReceptionEntries: cart.extra_reception,
          receptionEntries: cart.extra_reception,
          diversityLunchEntries: cart.diversity_inclusion
        }}
        ijcaiMembership={cart.ijcai_membership_purchase || null}
        memberDiscount={cart.member_discount || 0}
        memberDiscountSource={cart.member_discount_source || null}
        ppiDiscount={cart.ppi_discount || 0}
        ppiTier={cart.ppi_tier || null}
        price={{
          basePrice: cart.base_price,
          workshopTutorialPrice: cart.workshops_tutorials_price,
          additionalOptionsPrice: cart.additional_options_price,
          discountPrice: cart.discount,
          totalPrice: cart.total_price,

        }}
        selectedTutorials={['WS1', 'WS2']}
        selectedWorkshops={[]}
        className="w-full mt-4"
        expanded={expanded}
        collapsable={!expanded}
      />
    </>
  );
};

export default SummaryWrapper;


interface RegistrationSummaryProps {
  registrationCode: string
  registrationType: string
  accessType: string
  additionalOptions: {
    invitationLetterAdmin: boolean
    invitationLetterVisa: boolean
    banquetEntries: number
    studentReceptionEntries: number
    receptionEntries: number
    diversityLunchEntries: number
  }
  ijcaiMembership?: { type: string; price: number } | null
  memberDiscount?: number
  memberDiscountSource?: string | null
  ppiDiscount?: number
  ppiTier?: string | null
  price: {
    basePrice: number
    additionalOptionsPrice: number
    workshopTutorialPrice: number
    totalPrice: number
    discountPrice?: number
  }
  selectedWorkshops: string[]
  selectedTutorials: string[]
  className?: string
  expanded?: boolean
  collapsable?: boolean
}

function RegistrationSummary({
  registrationCode = "REG123",
  registrationType = "Full Delegate",
  accessType = "Full Conference",
  additionalOptions = {
    invitationLetterAdmin: false,
    invitationLetterVisa: false,
    banquetEntries: 0,
    studentReceptionEntries: 0,
    receptionEntries: 0,
    diversityLunchEntries: 0,
  },
  ijcaiMembership = null,
  memberDiscount = 0,
  memberDiscountSource = null,
  ppiDiscount = 0,
  ppiTier = null,
  price = {
    basePrice: 799,
    workshopTutorialPrice: 0,
    additionalOptionsPrice: 150,
    discountPrice: 0,
    totalPrice: 1280,
  },
  selectedWorkshops = [],
  selectedTutorials = [],
  className = "",
  expanded = false,
  collapsable = true,
}: RegistrationSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(expanded)

  const hasAdditionalOptions =
    additionalOptions.invitationLetterAdmin ||
    additionalOptions.invitationLetterVisa ||
    additionalOptions.banquetEntries > 0 ||
    additionalOptions.receptionEntries > 0 ||
    additionalOptions.diversityLunchEntries > 0 ||
    !!ijcaiMembership

  return (
    <div className={`w-full ${className}`}>
      {/* Collapsed view that matches the Total Price section in the image */}
      <div
        className={`flex justify-between items-center py-4 ${isExpanded ? "hidden" : "flex"}`}
        style={{ borderTop: "1px solid #e5e7eb" }}
      >
        <div>
          <h3 className="text-base font-medium">Total Price</h3>
          <p className="text-sm text-slate-500">Registration code #{registrationCode}</p>
        </div>

        <div className="flex items-center gap-4">
          {collapsable && (
            <button
              onClick={() => setIsExpanded(true)}
              className="text-sm text-blue-600 hover:underline flex items-center"
            >
              <Info size={16} className="mr-1" />
              View details
            </button>
          )}
          <span className="text-2xl font-bold">${price.totalPrice}</span>
        </div>

      </div>

      {/* Expanded view */}
      {isExpanded && (
        <div
          className="w-full overflow-hidden border border-slate-200 rounded-lg shadow-sm"
          style={{ borderTop: "1px solid #e5e7eb" }}
        >
          <div className="p-0">
            <div className="bg-blue-50 p-3 flex justify-between items-center">
              <h3 className="font-medium">Registration Summary</h3>
              {collapsable && (
                <button
                  onClick={() => setIsExpanded(false)}
                  className="text-sm text-blue-600 hover:underline flex items-center"
                  aria-label="Collapse summary"
                >
                  <ChevronUp size={18} className="mr-1" /> Hide details
                </button>
              )}
            </div>

            <div className="md:flex">
              {/* Left section - Basic Information */}
              <div className="p-4 md:w-1/3" style={{ borderRight: "1px solid #e5e7eb" }}>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-500">Registration Code</p>
                    <p className="font-mono font-medium">{registrationCode}</p>
                  </div>

                  <div>
                    <p className="text-sm text-slate-500">Registration Type</p>
                    <span className="inline-flex mt-1 text-sm border border-slate-200 px-2 py-1 rounded-full">
                      {registrationType}
                    </span>
                  </div>

                  <div>
                    <p className="text-sm text-slate-500">Access</p>
                    <span className="inline-flex mt-1 text-sm border border-slate-200 px-2 py-1 rounded-full">
                      {accessType}
                    </span>
                  </div>
                </div>
              </div>

              {/* Middle section - Additional Options */}
              <div className="p-4 md:w-1/3" style={{ borderRight: "1px solid #e5e7eb" }}>
                <p className="text-sm text-slate-500 mb-2">Additional Options</p>

                {!hasAdditionalOptions && (
                  <p className="text-sm text-slate-400 italic">No additional options selected</p>
                )}

                <div className="space-y-2">
                  {additionalOptions.invitationLetterAdmin && (
                    <div className="flex items-center text-sm">
                      <Check size={14} className="mr-2 text-green-600" />
                      <span>Invitation letter (administrative purposes)</span>
                    </div>
                  )}

                  {additionalOptions.invitationLetterVisa && (
                    <div className="flex items-center text-sm">
                      <Check size={14} className="mr-2 text-green-600" />
                      <span>Invitation letter for visa</span>
                    </div>
                  )}

                  {additionalOptions.banquetEntries > 0 && (
                    <div className="flex items-center text-sm">
                      <Check size={14} className="mr-2 text-green-600" />
                      <span> {additionalOptions.banquetEntries} additional ticket{(additionalOptions.banquetEntries > 1) ? "s" : ""} for banquet.</span>
                    </div>
                  )}

                  {additionalOptions.receptionEntries > 0 && (
                    <div className="flex items-center text-sm">
                      <Check size={14} className="mr-2 text-green-600" />
                      <span> {additionalOptions.receptionEntries} additional ticket{(additionalOptions.receptionEntries > 1) ? "s" : ""} for reception.</span>
                    </div>
                  )}

                  {additionalOptions.diversityLunchEntries > 0 && (
                    <div className="flex items-center text-sm">
                      <Check size={14} className="mr-2 text-green-600" />
                      <span> {additionalOptions.diversityLunchEntries} ticket{(additionalOptions.diversityLunchEntries > 1) ? "s" : ""} for the diversity and inclusion lunch.</span>
                    </div>
                  )}

                  {ijcaiMembership && (
                    <div className="flex items-center text-sm">
                      <Check size={14} className="mr-2 text-green-600" />
                      <span>IJCAI membership (3 yr {ijcaiMembership.type})</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right section - Price */}
              <div className="p-4 md:w-1/3 bg-slate-50">
                <p className="text-sm text-slate-500 mb-3">Price Summary</p>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Base Registration</span>
                    <span>${price.basePrice.toFixed(2)}</span>
                  </div>

                  {price.workshopTutorialPrice > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Workshops/Tutorials</span>
                      <span>${price.workshopTutorialPrice.toFixed(2)}</span>
                    </div>
                  )}
                  {(price.additionalOptionsPrice - (ijcaiMembership?.price ?? 0)) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Additional Items</span>
                      <span>${(price.additionalOptionsPrice - (ijcaiMembership?.price ?? 0)).toFixed(2)}</span>
                    </div>
                  )}
                  {ijcaiMembership && (
                    <div className="flex justify-between text-sm">
                      <span>IJCAI Membership</span>
                      <span>${ijcaiMembership.price.toFixed(2)}</span>
                    </div>
                  )}
                  {memberDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Membership discount</span>
                      <span>-${memberDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {ppiDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>
                        PPI Discount
                        {ppiTier ? ` (${ppiTier.charAt(0).toUpperCase() + ppiTier.slice(1)})` : ""}
                      </span>
                      <span>-${ppiDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {(price.discountPrice && (price.discountPrice - memberDiscount - ppiDiscount) > 0) ? (
                    <div className="flex justify-between text-sm">
                      <span>Discount </span>
                      <span>-${(price.discountPrice - memberDiscount - ppiDiscount).toFixed(2)}</span>
                    </div>
                  ) : <></>}

                  <div className="my-2 border-t border-slate-200"></div>

                  <div className="flex justify-between font-medium">
                    <span>Total</span>
                    <span className="text-xl font-bold">${price.totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
